import { useState, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectUserInformations } from '../features/authSlice';
import createBlogService from '../services/blogService';
import { showToast } from '../utils/utils';

export const useBlog = () => {
    const user = useSelector(selectUserInformations);
    const blogService = useMemo(() => createBlogService(user?.token), [user?.token]);

    const [posts, setPosts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [categoryLoading, setCategoryLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchCategories = useCallback(async () => {
        if (!user?.token) return;
        setCategoryLoading(true);
        try {
            const data = await blogService.getAllCategories();
            if (data.status === 200) {
                setCategories(data.data.items);
            }
        } catch (err) {
            console.error('Error fetching categories:', err);
            setError(err);
        } finally {
            setCategoryLoading(false);
        }
    }, [user?.token, blogService]);

    const fetchPosts = useCallback(async (categoryId = null, searchQuery = '') => {
        if (!user?.token) return;
        setLoading(true);
        try {
            const params = { search: searchQuery };
            let data;
            if (categoryId) {
                data = await blogService.getPostsByCategory(categoryId, params);
            } else {
                data = await blogService.getAllPosts(params);
            }

            if (data.status === 200) {
                setPosts(data.data.items);
            }
        } catch (err) {
            console.error('Error fetching posts:', err);
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [user?.token, blogService]);

    const createPost = useCallback(async (postData) => {
        if (!user?.token) return;
        setLoading(true);
        try {
            const data = await blogService.createPost(postData);
            return data;
        } catch (err) {
            console.error('Error creating post:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [user?.token, blogService]);

    const updatePost = useCallback(async (postId, postData) => {
        if (!user?.token) return;
        setLoading(true);
        try {
            const data = await blogService.updatePost(postId, postData);
            return data;
        } catch (err) {
            console.error('Error updating post:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [user?.token, blogService]);

    const deletePost = useCallback(async (postId) => {
        if (!user?.token) return;
        setLoading(true);
        try {
            const data = await blogService.deletePost(postId);
            setPosts(prev => prev.filter(p => p.id !== postId));
            return data;
        } catch (err) {
            console.error('Error deleting post:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [user?.token, blogService]);

    return {
        posts,
        categories,
        loading,
        categoryLoading,
        error,
        fetchCategories,
        fetchPosts,
        createPost,
        updatePost,
        deletePost
    };
};

export const usePostDetails = (postId) => {
    const user = useSelector(selectUserInformations);
    const blogService = useMemo(() => createBlogService(user?.token), [user?.token]);

    const [post, setPost] = useState(null);
    const [comments, setComments] = useState([]);
    const [reactions, setReactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [commentsLoading, setCommentsLoading] = useState(false);
    const [reactionLoading, setReactionLoading] = useState(false);
    const [totalComments, setTotalComments] = useState(0);

    const fetchPostData = useCallback(async () => {
        if (!postId || !user?.token) return;
        setLoading(true);
        try {
            const [postRes, commentsRes, reactionsRes] = await Promise.all([
                blogService.getPostDetails(postId),
                blogService.getPostComments(postId, { limit: 5 }),
                blogService.getPostReactions(postId)
            ]);

            setPost(postRes.data);
            setComments(commentsRes.data.items);
            setTotalComments(commentsRes.data.total);
            setReactions(reactionsRes.data.items);
        } catch (err) {
            console.error('Error fetching post details:', err);
        } finally {
            setLoading(false);
        }
    }, [postId, user?.token, blogService]);

    const loadMoreComments = useCallback(async (page) => {
        if (!postId || !user?.token) return;
        setCommentsLoading(true);
        try {
            const data = await blogService.getPostComments(postId, { page, limit: 5 });
            setComments(prev => [...prev, ...data.data.items]);
        } catch (err) {
            console.error('Error loading more comments:', err);
        } finally {
            setCommentsLoading(false);
        }
    }, [postId, user?.token, blogService]);

    const addComment = useCallback(async (content) => {
        if (!postId || !user?.token) return;
        try {
            const data = await blogService.addComment(postId, content);
            setComments(prev => [data.data, ...prev]);
            setTotalComments(prev => prev + 1);
            return data;
        } catch (err) {
            console.error('Error adding comment:', err);
            throw err;
        }
    }, [postId, user?.token, blogService]);

    const deleteComment = useCallback(async (commentId) => {
        if (!user?.token) return;
        try {
            await blogService.deleteComment(commentId);
            setComments(prev => prev.filter(c => c.id !== commentId));
            setTotalComments(prev => prev - 1);
        } catch (err) {
            console.error('Error deleting comment:', err);
            throw err;
        }
    }, [user?.token, blogService]);

    const toggleReaction = useCallback(async (reactionType) => {
        if (!postId || !user?.token || reactionLoading) return;
        setReactionLoading(true);
        try {
            const existingReaction = reactions.find(r => r.user_uuid === user.uuid);

            if (existingReaction) {
                await blogService.removeReactionFromPost(postId, existingReaction.type);
                if (existingReaction.type === reactionType) {
                    setReactions(prev => prev.filter(r => r.user_uuid !== user.uuid));
                    return;
                }
            }

            await blogService.addReactionToPost(postId, reactionType);
            const newReaction = {
                id: Date.now(),
                type: reactionType,
                user_uuid: user.uuid,
                user: user
            };
            setReactions(prev => [...prev.filter(r => r.user_uuid !== user.uuid), newReaction]);
        } catch (err) {
            console.error('Error toggling reaction:', err);
            showToast("Oops", "Could not update reaction.", "error");
        } finally {
            setReactionLoading(false);
        }
    }, [postId, user, reactions, blogService, reactionLoading]);

    const toggleCommentReaction = useCallback(async (commentId, reactionType) => {
        if (!user?.token || reactionLoading) return;
        setReactionLoading(true);
        try {
            const comment = comments.find(c => c.id === commentId);
            const existingReaction = comment?.reactions?.find(r => r.user_uuid === user.uuid);

            let newCountChange = 1;

            if (existingReaction) {
                await blogService.removeReactionFromComment(commentId, existingReaction.type);
                if (existingReaction.type === reactionType) {
                    setComments(prev => prev.map(c =>
                        c.id === commentId
                            ? { ...c, reactions: c.reactions.filter(r => r.user_uuid !== user.uuid), reactions_count: Math.max(0, (c.reactions_count || 1) - 1) }
                            : c
                    ));
                    return;
                }
                // If it was a different type, we just removed one, and we are about to add one.
                // So the count change will be 0 (-1 + 1)
                newCountChange = 0;
            }

            await blogService.addReactionToComment(commentId, reactionType);
            setComments(prev => prev.map(c =>
                c.id === commentId
                    ? {
                        ...c,
                        reactions: [...(c.reactions || []).filter(r => r.user_uuid !== user.uuid), { type: reactionType, user_uuid: user.uuid }],
                        reactions_count: (c.reactions_count || 0) + newCountChange
                    }
                    : c
            ));
        } catch (err) {
            console.error('Error toggling comment reaction:', err);
            showToast("Oops", "Could not update reaction.", "error");
        } finally {
            setReactionLoading(false);
        }
    }, [user, comments, blogService, reactionLoading]);

    const deletePost = useCallback(async () => {
        if (!postId || !user?.token) return;
        setLoading(true);
        try {
            const data = await blogService.deletePost(postId);
            return data;
        } catch (err) {
            console.error('Error deleting post:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [postId, user?.token, blogService]);

    return {
        post,
        comments,
        reactions,
        loading,
        commentsLoading,
        totalComments,
        fetchPostData,
        loadMoreComments,
        addComment,
        deleteComment,
        toggleReaction,
        toggleCommentReaction,
        deletePost
    };
};
